import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import {
  Close as CloseIcon,
  Speed as SpeedIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { formatDateDMY, formatAccuracy } from '../../utils/dateFormat.ts';
import React from 'react';

const USE_CASES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

interface ViewUseCaseDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, shown in the header instead of useCase.id (e.g. table display ID like UC-001). */
  displayId?: string;
  useCase: {
    id: string;
    name: string;
    modelFile: string;
    accuracy: number;
    description: string;
    uploadedOn: Date;
    isFavorite?: boolean;
    imagePreview?: string;
    useCaseType?: string;
    zoneType?: string;
  } | null;
}

/** Resolve image URL: use imagePreview (from API image_url) or fallback to /use-cases/:id/image */
function getUseCaseImageSrc(useCase: ViewUseCaseDialogProps['useCase']): string | null {
  if (!useCase) return null;
  if (useCase.imagePreview) return useCase.imagePreview;
  const base = USE_CASES_API_BASE.replace(/\/$/, '');
  if (base && useCase.id) return `${base}/use-cases/${useCase.id}/image`;
  return null;
}

export function ViewUseCaseDialog({ open, onClose, displayId, useCase }: ViewUseCaseDialogProps) {
  if (!useCase) return null;

  const imageSrc = getUseCaseImageSrc(useCase);

  const getAccuracyColor = (accuracy: number) => {
    const v = accuracy <= 1 ? accuracy * 100 : accuracy;
    if (v >= 95) return '#10B981';
    if (v >= 90) return '#F59E0B';
    return '#EF4444';
  };
  const getAccuracyLabel = (accuracy: number) => {
    const v = accuracy <= 1 ? accuracy * 100 : accuracy;
    if (v >= 95) return 'Excellent';
    if (v >= 90) return 'Good';
    return 'Fair';
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 900,
          maxHeight: '90vh',
          borderRadius: '10px',
          boxShadow: '0px 25px 50px 0px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '0.8px solid #E5E7EB',
          bgcolor: '#F9FAFB',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: '#2932E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CategoryIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '18px',
                fontWeight: 600,
                color: '#101828',
              }}
            >
              {useCase.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                color: '#64748B',
              }}
            >
              {displayId ?? useCase.id}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: '#99A1AF',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content - 2 Column Layout */}
      <Box sx={{ display: 'flex', p: 3, gap: 3 }}>
        {/* Left Column - Image & Stats */}
        <Box sx={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Image Preview */}
          <Box
            sx={{
              width: '100%',
              height: 180,
              borderRadius: '8px',
              overflow: 'hidden',
              bgcolor: '#F5F5F5',
              border: '1px solid #E5E7EB',
            }}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={useCase.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#E5E7EB',
                  color: '#6B7280',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                }}
              >
                No image
              </Box>
            )}
          </Box>

          {/* Accuracy Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SpeedIcon sx={{ fontSize: 18, color: getAccuracyColor(useCase.accuracy) }} />
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#64748B',
                  textTransform: 'uppercase',
                }}
              >
                Detection Accuracy
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '32px',
                  fontWeight: 700,
                  color: getAccuracyColor(useCase.accuracy),
                  lineHeight: 1,
                }}
              >
                {formatAccuracy(useCase.accuracy)}
              </Typography>
              <Chip
                label={getAccuracyLabel(useCase.accuracy)}
                size="small"
                sx={{
                  height: 20,
                  bgcolor: getAccuracyColor(useCase.accuracy),
                  color: 'white',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>

          {/* Upload Date Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              bgcolor: '#EFF6FF',
              border: '1px solid #BFDBFE',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: '#2932E5' }} />
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#64748B',
                  textTransform: 'uppercase',
                }}
              >
                Uploaded On
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2932E5',
              }}
            >
              {formatDateDMY(new Date(useCase.uploadedOn))}
            </Typography>
          </Box>
        </Box>

        {/* Right Column - Details */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Use Case Type & Zone Type Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                }}
              >
                Use Case Type
              </Typography>
              <Chip
                label={useCase.useCaseType || 'Not specified'}
                sx={{
                  bgcolor: '#EFF6FF',
                  color: '#2932E5',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  height: 32,
                  borderRadius: '6px',
                  border: '1px solid #BFDBFE',
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                }}
              >
                Zone Type
              </Typography>
              <Chip
                label={useCase.zoneType || 'Not specified'}
                sx={{
                  bgcolor: '#F0FDF4',
                  color: '#10B981',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  height: 32,
                  borderRadius: '6px',
                  border: '1px solid #BBF7D0',
                }}
              />
            </Box>
          </Box>

          {/* Model File */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
              }}
            >
              Model File
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto Mono, monospace',
                  fontSize: '13px',
                  color: '#334155',
                  wordBreak: 'break-all',
                }}
              >
                {useCase.modelFile}
              </Typography>
            </Box>
          </Box>

          {/* Description - full width of section */}
          <Box sx={{ flex: 1, width: '100%', maxWidth: '100%' }}>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
              }}
            >
              Description
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '8px',
                bgcolor: '#FAFAFA',
                border: '1px solid #E5E7EB',
                width: '100%',
                minHeight: 80,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#475569',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}
              >
                {useCase.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 3,
          py: 2,
          bgcolor: '#F9FAFB',
          borderTop: '0.8px solid #E5E7EB',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            bgcolor: '#2932E5',
            border: '0.8px solid #2932E5',
            borderRadius: '8px',
            color: 'white',
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 3,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1f28b8',
              border: '0.8px solid #1f28b8',
              boxShadow: 'none',
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
}